import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ClockCounterClockwise, Trash, PencilSimple, Copy, Check, FacebookLogo, WhatsappLogo, MapPin, Wrench, Clock, X, FloppyDisk, ShareNetwork } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PostHistory() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const fetchPosts = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/posts`, { withCredentials: true }); setPosts(data); }
    catch (error) { console.error("Posts fetch error:", error.message); toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleDelete = async (postId) => {
    if (!window.confirm('Supprimer ce post ?')) return;
    try { await axios.delete(`${API}/posts/${postId}`, { withCredentials: true }); setPosts(prev => prev.filter(p => p.id !== postId)); toast.success('Post supprimé'); }
    catch { toast.error('Erreur de suppression'); }
  };

  const startEdit = (post) => { setEditingId(post.id); setEditContent(post.content); };
  const cancelEdit = () => { setEditingId(null); setEditContent(''); };

  const saveEdit = async (postId) => {
    try { const { data } = await axios.put(`${API}/posts/${postId}`, { content: editContent }, { withCredentials: true }); setPosts(prev => prev.map(p => p.id === postId ? { ...p, ...data } : p)); setEditingId(null); toast.success('Post mis à jour'); }
    catch { toast.error('Erreur de mise à jour'); }
  };

  const handleCopy = async (content, postId) => {
    try { await navigator.clipboard.writeText(content); setCopiedId(postId); toast.success('Copié !'); setTimeout(() => setCopiedId(null), 2000); }
    catch { toast.error('Impossible de copier'); }
  };

  const handlePublish = async (postId) => {
    try { await axios.post(`${API}/posts/${postId}/publish`, {}, { withCredentials: true }); setPosts(prev => prev.map(p => p.id === postId ? { ...p, published_to_gallery: true } : p)); toast.success('Publié en galerie !'); }
    catch { toast.error('Erreur de publication'); }
  };

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };

  if (loading) return (
    <div className="flex items-center justify-center py-16" data-testid="history-loading">
      <div className="animate-spin w-6 h-6 border-2 border-[#3F3F46] border-t-[#8B5CF6] rounded-full" />
    </div>
  );

  if (posts.length === 0) return (
    <div className="text-center py-16 animate-fade-in" data-testid="history-empty">
      <img src="https://images.unsplash.com/photo-1615974679600-665fb9468c4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxjYXJwZW50ZXIlMjBtZWFzdXJpbmclMjB3b29kfGVufDB8fHx8MTc3NjEzMDA3MXww&ixlib=rb-4.1.0&q=85"
        alt="Empty" className="w-32 h-32 mx-auto rounded-full object-cover mb-4 opacity-40" />
      <p className="text-[#A1A1AA] font-medium">Aucun post pour le moment</p>
      <p className="text-sm text-[#71717A] mt-1">Générez votre premier post marketing !</p>
    </div>
  );

  return (
    <div className="space-y-3 animate-fade-in" data-testid="post-history">
      <div className="flex items-center gap-2 mb-4">
        <ClockCounterClockwise size={20} weight="duotone" className="text-[#71717A]" />
        <span className="text-sm font-medium text-[#A1A1AA]">{posts.length} post{posts.length > 1 ? 's' : ''}</span>
      </div>
      {posts.map((post, index) => (
        <div key={post.id} className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: `${index * 0.05}s` }} data-testid={`history-post-${post.id}`}>
          <div className="px-4 py-2.5 border-b border-[#27272A] flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-[#A1A1AA] flex-wrap">
              {post.platform === 'facebook' ? <FacebookLogo size={14} weight="duotone" className="text-blue-400" /> : <WhatsappLogo size={14} weight="duotone" className="text-green-400" />}
              <span className="flex items-center gap-1"><MapPin size={12} weight="duotone" />{post.city}</span>
              <span className="text-[#3F3F46]">|</span>
              <span className="flex items-center gap-1"><Wrench size={12} weight="duotone" />{post.construction_type}</span>
              <span className="text-[#3F3F46]">|</span>
              <span className="flex items-center gap-1"><Clock size={12} weight="duotone" />{formatDate(post.created_at)}</span>
              {post.mode === 'avant_apres' && <span className="px-1.5 py-0.5 bg-[#8B5CF6]/15 text-[#A78BFA] rounded text-[10px] font-bold">AVANT/APRÈS</span>}
            </div>
          </div>
          <div className="p-4">
            {editingId === post.id ? (
              <div className="space-y-3">
                <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={6}
                  className="w-full p-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#E4E4E7] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none resize-none text-sm"
                  data-testid={`edit-textarea-${post.id}`} />
                <div className="flex gap-2 justify-end">
                  <button onClick={cancelEdit} className="h-9 px-4 text-sm border border-[#27272A] rounded-lg hover:bg-[#27272A] text-[#A1A1AA] flex items-center gap-1.5" data-testid={`cancel-edit-${post.id}`}>
                    <X size={14} /> Annuler
                  </button>
                  <button onClick={() => saveEdit(post.id)} className="h-9 px-4 text-sm bg-[#8B5CF6] text-white font-semibold rounded-lg hover:bg-[#7C3AED] flex items-center gap-1.5" data-testid={`save-edit-${post.id}`}>
                    <FloppyDisk size={14} weight="duotone" /> Sauvegarder
                  </button>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#E4E4E7]" data-testid={`post-text-${post.id}`}>{post.content}</p>
            )}
          </div>
          {editingId !== post.id && (
            <div className="px-4 py-2.5 border-t border-[#27272A] flex items-center justify-end gap-1.5">
              <button onClick={() => handleCopy(post.content, post.id)} data-testid={`copy-btn-${post.id}`}
                className={`h-8 px-3 text-xs rounded-lg flex items-center gap-1.5 transition-all ${copiedId === post.id ? 'bg-green-500/15 text-green-400' : 'bg-[#27272A] text-[#A1A1AA] hover:bg-[#3F3F46]'}`}>
                {copiedId === post.id ? <Check size={14} /> : <Copy size={14} />} {copiedId === post.id ? 'Copié' : 'Copier'}
              </button>
              {!post.published_to_gallery && (
                <button onClick={() => handlePublish(post.id)} className="h-8 px-3 text-xs bg-[#27272A] text-[#A1A1AA] rounded-lg hover:bg-[#3F3F46] flex items-center gap-1.5" data-testid={`publish-btn-${post.id}`}>
                  <ShareNetwork size={14} /> Galerie
                </button>
              )}
              <button onClick={() => startEdit(post)} className="h-8 px-3 text-xs bg-[#27272A] text-[#A1A1AA] rounded-lg hover:bg-[#3F3F46] flex items-center gap-1.5" data-testid={`edit-btn-${post.id}`}>
                <PencilSimple size={14} /> Modifier
              </button>
              <button onClick={() => handleDelete(post.id)} className="h-8 px-3 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 flex items-center gap-1.5" data-testid={`delete-btn-${post.id}`}>
                <Trash size={14} /> Supprimer
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
